// ============================================================================
// JARVISTRAVEL - FI EXPLANATION (v2 — plain language)
// Version: 4.0.0
// The Fatigue Index, explained the way a person would: a clear 1–9 score on the
// temperature gauge, then "why this day feels the way it does" and "what would
// help" — in human language, never hidden-state variable names (FIS FE §12.2).
// Fed by fiEngine.scoreDay() now; a real /score-day endpoint (JAR-135) later.
// ============================================================================

import { memo } from 'react';
import { TrendingUp, Lightbulb, Leaf, Check, Sparkles, type LucideIcon } from 'lucide-react';
import type { FatigueLevel } from '@/app/types';
import { fiBand, fiBandLabel, fiDescriptor, fiToneForScore, clampFi, type FIBand } from '@/app/utils/fi';
import { capitalizeFirst as cap } from '@/app/utils';
import { FIMeter } from './FIBadge';
import { OfflineNotice } from './OfflineNotice';
import { isDemoSession } from '@/app/api/client';

// Band-aware heading for the "why" — calm, never judgmental.
const WHY_HEADING: Record<FIBand, string> = {
  chill: 'Why today’s easy',
  balanced: 'Why it’s full but manageable',
  packed: 'Why today runs heavy',
};

export interface FIExplanationProps {
  /** The public 1–9 score. */
  score: number;
  /** Band label (Chill / Balanced / Packed). */
  level: FatigueLevel;
  /** Plain-language reasons the day feels this way (fiEngine.scoreDay top_drivers). */
  drivers: string[];
  /** Plain-language "what would help" (fiEngine.scoreDay mitigations). */
  mitigations?: string[];
  /** Protected recovery windows detected in the day. */
  recovery?: string[];
  /** Optional context label (e.g. "Wed, Jul 29 · Vatican City"). */
  dayLabel?: string;
  /** Override note — the user chose to keep a heavier plan. */
  overrideAccepted?: boolean;
  overrideTo?: string;
  /** Optional primary action (e.g. "Make this day lighter"). */
  action?: { label: string; icon?: LucideIcon; onClick: () => void };
}

export const FIExplanation = memo(function FIExplanation({
  score,
  level,
  drivers,
  mitigations = [],
  recovery = [],
  dayLabel,
  overrideAccepted = false,
  overrideTo,
  action,
}: FIExplanationProps) {
  const s = clampFi(score);
  const band = fiBand(s);
  const tone = fiToneForScore(s);
  const ActionIcon = action?.icon ?? Sparkles;

  return (
    <div className="space-y-5">
      {/* Score + gauge — the number made instantly legible */}
      <div className={`rounded-2xl p-5 ${tone.bg}`}>
        <div className="flex items-center gap-4">
          <div className="text-center shrink-0">
            <p className={`text-5xl font-bold leading-none tabular-nums ${tone.text}`}>{s}</p>
            <p className="text-[11px] font-medium text-muted mt-1">out of 9</p>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-lg font-semibold ${tone.text}`}>{level ?? fiBandLabel(band)}</p>
            <p className="text-sm text-muted">{cap(fiDescriptor(band))}</p>
            {dayLabel && <p className="text-xs text-muted mt-0.5 truncate">{dayLabel}</p>}
          </div>
        </div>
        <FIMeter score={s} showZones className="mt-4" />
      </div>

      {/* Core's rating is the true one (JAR-1678). Offline, the app shows its own
          rating of the same formula, and says it may change. Renders nothing online.

          Not in the demo: the demo never asks Core at all, so "it may update
          once you're back online" would be telling the reader something that is
          not going to happen. */}
      {!isDemoSession() && (
        <OfflineNotice message="Can’t reach our servers, so this rating was worked out on your device. It may update once you’re back online." />
      )}

      {/* Override note */}
      {overrideAccepted && (
        <div className="p-3 bg-fi-balanced/10 border border-fi-balanced/20 rounded-xl">
          <p className="text-sm text-fi-balanced flex items-center gap-2">
            <Check className="w-4 h-4 shrink-0" strokeWidth={2.5} />
            <span>You chose to keep this pace{overrideTo ? ` (${overrideTo})` : ''}.</span>
          </p>
        </div>
      )}

      {/* Why this day feels the way it does */}
      {drivers.length > 0 && (
        <section className="space-y-2.5">
          <h4 className="font-semibold text-ink flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-muted" strokeWidth={2} />
            {WHY_HEADING[band]}
          </h4>
          <ul className="space-y-2">
            {drivers.map((d, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-body">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${tone.dot}`} aria-hidden />
                <span>{cap(d)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* What would help */}
      {mitigations.length > 0 && (
        <section className="space-y-2.5">
          <h4 className="font-semibold text-ink flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-fi-balanced" strokeWidth={2} />
            What would help
          </h4>
          <ul className="space-y-2">
            {mitigations.map((m, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-body">
                <Check className="w-4 h-4 mt-0.5 text-fi-balanced shrink-0" strokeWidth={2.5} aria-hidden />
                <span>{cap(m)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Built-in recovery */}
      {recovery.length > 0 && (
        <section className="rounded-xl bg-fi-chill/10 p-3.5">
          <h4 className="text-sm font-semibold text-fi-chill flex items-center gap-2 mb-2">
            <Leaf className="w-4 h-4" strokeWidth={2} />
            Built-in recovery
          </h4>
          <ul className="space-y-1.5">
            {recovery.map((r, i) => (
              <li key={i} className="text-xs text-body flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-fi-chill shrink-0" aria-hidden />
                {/* Its own box, so a stop's name can shrink beside the dot (JAR-3047). */}
                <span className="min-w-0 break-words">{cap(r)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Primary action */}
      {action && (
        <button
          onClick={action.onClick}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand text-brand-fg font-semibold py-3 px-4 active:scale-[0.99] transition-transform"
        >
          <ActionIcon className="w-4 h-4" strokeWidth={2} />
          {action.label}
        </button>
      )}
    </div>
  );
});
